import { z } from 'zod'
import type { Task } from './tasks'

/**
 * The portable screen (จอพกพา): a tablet or phone that is one person's, where what their forms ask for is done while
 * they work on their own computer — someone at the counter signs, a photo is taken. It opens its own site
 * (screen.<domain>, apps/screen), an app of its own to install with no one signed in, and is paired by scanning the QR
 * its person shows or typing the code under it (`SCREEN_PAIRING_SECONDS`). One screen a person: pairing another
 * replaces it, and one not used for `SCREEN_IDLE_HOURS` is unpaired. Tasks one asks of oneself go to it (tasks.ts).
 */
/** How long a pairing's QR and code can be used, once */
export const SCREEN_PAIRING_SECONDS = 120
export const SCREEN_CODE_LENGTH = 6
/** A screen not used this long is unpaired: paired each morning, in practice */
export const SCREEN_IDLE_HOURS = 12

/** A device pairing itself: by the code typed or the QR's token, with a name for its person to tell it by */
export const screenPairInput = z
  .object({
    code: z
      .string()
      .regex(new RegExp(`^\\d{${SCREEN_CODE_LENGTH}}$`))
      .optional(),
    token: z.string().min(20).max(100).optional(),
    name: z.string().trim().max(80).default(''),
  })
  .refine((input) => !!input.code !== !!input.token, { message: 'ใส่รหัสหรือสแกน QR' })
export type ScreenPairInput = z.input<typeof screenPairInput>

/** A pairing its person shows: the QR's link carries the token, the code is typed on a device without a camera */
export interface ScreenPairing {
  code: string
  token: string
  expiresAt: string
}

/** One's screen as one's own apps see it */
export interface PairedScreen {
  name: string
  pairedAt: string
  /** Open now (connected) */
  online: boolean
}

/** What a screen sees: whose it is, and what waits for it there, the latest first */
export interface ScreenState {
  owner: { name: string }
  asks: Task[]
}

/**
 * What the server tells while one listens (`/api/me/events`, `/api/screen/events`): a hint to fetch again, never the
 * data itself. On (re)connecting: fetch what may have been missed. To a person: a task they asked changed, or their
 * screen did, or a chat room of theirs (a message, an edit, someone reading). To a screen: what waits there changed, or
 * it was unpaired
 */
export type LiveEvent =
  | { type: 'hello' }
  | { type: 'task'; id: string }
  | { type: 'screen' }
  | { type: 'asks' }
  | { type: 'unpaired' }
  | { type: 'chat'; roomId: string }
